import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';

import { ReplaySubject, Observable } from 'rxjs';
import { takeUntil, filter, map } from 'rxjs/operators';
import { UbiAuthStateType } from 'uber-ui';

import { SnackbarService } from 'app/services/snackbar.service';

import { LoginResponse, ICredentials, ICompleteFirstLogin, IResetPassword, IMfaConfirmLogin } from 'modules/shared/models/auth.models';
import { PASSWORD_PATTERN } from 'modules/shared/constants/password-pattern.constants';
import { MFA_CODE_PATTERN } from 'modules/shared/constants/mfa-code-pattern.constants';

import { AppState, Selectors } from 'store/root-reducer';
import { LogIn } from 'store/actions/auth.actions';
import * as AuthActions from 'store/actions/auth.actions';

@Component({
  selector: 'artemis-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {

  private unsubscribe$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private challengeUser: any;
  private passwordPattern: string =  PASSWORD_PATTERN;
  private mfaCodePattern: string = MFA_CODE_PATTERN;

  loading$: Observable<boolean>;
  challengeName: UbiAuthStateType;
  ninetyType: UbiAuthStateType.ninetyDaysChangePasswordRequired |
              UbiAuthStateType.ninetyDaysBlocked |
              UbiAuthStateType.resetPasswordRequired;

  loginForm: FormGroup;
  completeLoginForm: FormGroup;
  resetPasswordForm: FormGroup;
  mfaConfirmForm: FormGroup;

  constructor(private store: Store<AppState>,
              private formBuilder: FormBuilder,
              private snackbar: SnackbarService) { }

  ngOnInit() {
    this.buildForms();
    this.initLoading();
    this.subscribeLogin();
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();

    this.store.dispatch(new AuthActions.ClearLoginChallengeRequired());
    this.store.dispatch(new AuthActions.ClearCompleteFirstLogIn());
    this.store.dispatch(new AuthActions.ClearForgotPassword());
    this.store.dispatch(new AuthActions.ClearConfirmNinetyDaysPassword());
    this.store.dispatch(new AuthActions.ClearResetPassword());
    this.store.dispatch(new AuthActions.ClearMfaConfirmLogin());
  }

  /**
   * Inits observables for loading spinner
   * @method initLoading
   */
  private initLoading(): void {
    this.loading$ = this.store.select(Selectors.auth.getLoading)
      .pipe(
        takeUntil(this.unsubscribe$),
        map((loadingActionsList: Array<AuthActions.AuthActionTypes>) => {
          return loadingActionsList.includes(AuthActions.AuthActionTypes.LOGIN ||
                                             AuthActions.AuthActionTypes.LOGIN_CHALLENGE_REQUIRED ||
                                             AuthActions.AuthActionTypes.COMPLETE_FIRST_LOGIN ||
                                             AuthActions.AuthActionTypes.FORGOT_PASSWORD ||
                                             AuthActions.AuthActionTypes.CONFIRM_NINETY_DAYS_PASSWORD ||
                                             AuthActions.AuthActionTypes.RESET_PASSWORD);
        })
      );
  }

  /**
   * Build form for login
   * @method buildForms
   */
  private buildForms(): void {
    this.loginForm = this.formBuilder.group({
      username: new FormControl('', [Validators.email, Validators.required]),
      password: new FormControl('', [Validators.required])
    });

    this.completeLoginForm = this.formBuilder.group({
      newPassword: new FormControl('', [Validators.required, Validators.pattern(this.passwordPattern)]),
      confirmNewPassword: new FormControl('', [Validators.required, Validators.pattern(this.passwordPattern)])
    });

    this.resetPasswordForm = this.formBuilder.group({
      code: new FormControl('', [Validators.required, Validators.pattern(this.mfaCodePattern)]),
      newPassword: new FormControl('', [Validators.required, Validators.pattern(this.passwordPattern)]),
      confirmNewPassword: new FormControl('', [Validators.required, Validators.pattern(this.passwordPattern)])
    });

    this.mfaConfirmForm = this.formBuilder.group({
      code: new FormControl('', [Validators.required, Validators.pattern(this.mfaCodePattern)])
    });
  }

  /**
   * Subscribe login state
   * @method subscribeLogin
   */
  private subscribeLogin(): void {
    // Subsribe Login Challenge
    this.store.select(Selectors.auth.loginChallengeRequired)
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((res) => res !== null)
      ).subscribe((loginChallengeResponse: LoginResponse) => {
        this.challengeUser = loginChallengeResponse.user;
        switch (loginChallengeResponse.challenge) {
          case UbiAuthStateType.newPasswordRequired: {
            this.challengeName = UbiAuthStateType.newPasswordRequired;
            this.snackbar.error('Please set a new password to continue to access the system');
            break;
          }
          case UbiAuthStateType.resetPasswordRequired: {
            this.challengeName = null;
            this.ninetyType = UbiAuthStateType.resetPasswordRequired;
            break;
          }
          case UbiAuthStateType.ninetyDaysChangePasswordRequired: {
            this.challengeName = null;
            this.ninetyType = UbiAuthStateType.ninetyDaysChangePasswordRequired;
            break;
          }
          case UbiAuthStateType.ninetyDaysBlocked: {
            this.challengeName = null;
            this.ninetyType = UbiAuthStateType.ninetyDaysBlocked;
            break;
          }
          case UbiAuthStateType.mfaRequired: {
            this.challengeName = UbiAuthStateType.mfaRequired;
            break;
          }
          default: {
            break;
          }
        }
      });

    // Subscribe confirm ninety days password
    this.store.select(Selectors.auth.confirmNinetyDaysPassword)
      .pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe((confirmNinetyDaysPasswordResponse: any) => {
        if (confirmNinetyDaysPasswordResponse) {
          this.challengeName = UbiAuthStateType.ninetyDaysChangePasswordRequired;
          this.ninetyType = null;
        }
      });

    // Subscribe forgot password
    this.store.select(Selectors.auth.forgotPassword)
      .pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe((response: any) => {
        if (response) {
          this.challengeName = UbiAuthStateType.resetPasswordRequired;
          this.ninetyType = null;
        }
      });

    // Subscribe complete first login
    this.store.select(Selectors.auth.completeFirstLogin)
      .pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe((completeFirstLoginResponse: any) => {
        if (completeFirstLoginResponse) {
          this.challengeName = null;
          this.loginForm.reset();
          this.completeLoginForm.reset();
        }
      });

    // Subscribe reset password
    this.store.select(Selectors.auth.resetPassword)
      .pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe((response: any) => {
        if (response) {
          this.challengeName = null;
          this.loginForm.reset();
          this.resetPasswordForm.reset();
        }
      });

    // Subscribe login form to hide message challenge
    this.loginForm.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe(() => {
        this.ninetyType = null;
      });
  }

  /**
   * Submit login
   * @method onSubmit
   */
  onSubmit(): void {
    if (this.loginForm.invalid) {
      return;
    }

    this.ninetyType = null;
    const payload: ICredentials = {
      username: this.loginForm.value.username,
      password: this.loginForm.value.password
    };
    this.store.dispatch(new LogIn(payload));
  }

  /**
   * Complete first login
   * @method completeFirstLogin
   */
  completeFirstLogin(): void {
    if (this.completeLoginForm.invalid) {
      return;
    }
    if ((this.completeLoginForm.value.newPassword !== this.completeLoginForm.value.confirmNewPassword)) {
      this.snackbar.error('New and confirmation passwords do not match');
      return;
    }

    const payload: ICompleteFirstLogin = {
      user: this.challengeUser,
      newPassword: this.completeLoginForm.value.newPassword
    };
    this.store.dispatch(new AuthActions.CompleteFirstLogIn(payload));
  }

  /**
   * Reset Password
   * @method resetPassword
   */
  resetPassword(): void {
    if (this.resetPasswordForm.invalid) {
      return;
    }
    if ((this.resetPasswordForm.value.newPassword !== this.resetPasswordForm.value.confirmNewPassword)) {
      this.snackbar.error('New and confirmation passwords do not match');
      return;
    }

    const payload: IResetPassword = {
      ...this.resetPasswordForm.value,
      username: this.challengeUser.username
    };

    this.store.dispatch(new AuthActions.ResetPassword(payload));
  }

  /**
   * Confirm mfa required
   * @method confirmMfaLogin
   */
  confirmMfaLogin(): void {
    if (this.mfaConfirmForm.invalid) {
      return;
    }

    const payload: IMfaConfirmLogin = {
      ...this.mfaConfirmForm.value,
      username: this.challengeUser.username
    };

    this.store.dispatch(new AuthActions.MfaConfirmLogin(payload));
    this.mfaConfirmForm.setErrors({ invalid: true });
  }

  /**
   * Returns flag show materror or no
   * @method showFormControlErrors
   */
  showFormControlErrors(form: FormGroup, formControlName: string): boolean {
    return form.get(formControlName).invalid && (form.get(formControlName).dirty || form.get(formControlName).touched);
  }

  /**
   * Go back login page
   * @method goBack
   */
  goBack(): void {
    this.challengeName = null;
  }

  /**
   * Call confirm reset password
   * @method resetNow
   */
  resetNow(): void {
    if (this.ninetyType === UbiAuthStateType.ninetyDaysChangePasswordRequired) {
      this.store.dispatch(new AuthActions.ConfirmNinetyDaysPassword({ user: this.challengeUser }));
    } else if (this.ninetyType === UbiAuthStateType.resetPasswordRequired) {
      this.store.dispatch(new AuthActions.ForgotPassword({ email: this.challengeUser.username }));
    }
  }
}
